import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { trimPrTrendDefaultWindow, buildPrBackendDownloadCsvFileName, formatPrTrendForChart, formatPrMentionStatsTimeline } from '../../_utils';
import type { PrMetricEventData, PrMetricChartEventSetting } from '../PrMetricTtrend/types';
import PrMetricTrendChart from '../PrMetricTtrend';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import type { ParamsTimeType } from '@op/types/common';
import * as FileSaver from 'file-saver';
import './index.scss';

export interface IPrTrendSectionProps {
  data?: Record<string, unknown>;
  dataKey?: string;
  /** 事件时间线数据的 key（对应另一个 Service 的 dataKey） */
  eventDataKey?: string;
  titleEn?: string;
  titleCn?: string;
  metricNameEn?: string;
  metricNameCn?: string;
  subTitleEn?: string;
  subTitleCn?: string;
  chartHeight?: number;
  /** 指标格式化规则（tooltip、柱顶 label 等） */
  formatRule?: string;
  /** Y 轴格式化规则（不传则与 formatRule 一致） */
  yaxFormatRule?: string;
  /** 筛选器值（含 time_granularity） */
  filterVal?: {
    time_granularity?: string;
  };
  /** 用户是否手动改过日期筛选器；true 时趋势图不再裁剪默认窗口 */
  isDateChanged?: boolean;
  /** hover 事件标签是否展开 description 黑底气泡；默认 false */
  showEventDescriptionBubble?: boolean;
  /** 标题右侧「事件」显隐开关：默认 true 且与接口是否有事件无关；传 false 整段隐藏 */
  eventVisibilityToggleVisible?: boolean;
}

type PrTrendSeriesItem = { name: string; data: number[]; color?: string; };

type PrTrendPayload = {
  categories?: string[];
  series?: PrTrendSeriesItem[];
  title?: string;
  subTitle?: string;
};

type PrTrendServiceBlock = {
  data?: unknown;
  loading?: boolean;
  errorMsg?: string;
};

type PrEventTimelineBlock = {
  data?: {
    eventData?: PrMetricEventData;
    chartEventSetting?: PrMetricChartEventSetting;
  };
  loading?: boolean;
  errorMsg?: string;
};

const PrTrendSection: React.FC<IPrTrendSectionProps> = (props) => {
  const {
    data,
    dataKey = 'prSentimentTrend',
    eventDataKey,
    titleEn = 'Buzz & sentiment trend',
    titleCn = '声量与情感趋势',
    metricNameEn = 'Articles',
    metricNameCn = '独立站文章数',
    subTitleEn,
    subTitleCn,
    chartHeight = 340,
    formatRule = 'thousandsInit',
    yaxFormatRule,
    filterVal,
    isDateChanged,
    showEventDescriptionBubble,
    eventVisibilityToggleVisible,
  } = props;

  /**
   * Dashboard `Service` 只向子节点传 `data`、`filterLoading`，筛选在 `data.filter`；
   * 优先用 props.filterVal，并与 `data.filter` 合并得到 time_granularity。
   */
  const resolvedFilterVal = useMemo((): IPrTrendSectionProps['filterVal'] | undefined => {
    const fromData: Record<string, unknown> =
      data?.filter && typeof data.filter === 'object' && !Array.isArray(data.filter)
        ? (data.filter as Record<string, unknown>)
        : {};
    const fromProps: Record<string, unknown> =
      filterVal && typeof filterVal === 'object' ? (filterVal as Record<string, unknown>) : {};
    const merged: Record<string, unknown> = { ...fromData, ...fromProps };
    if (Object.keys(merged).length === 0) {
      return undefined;
    }
    const out: IPrTrendSectionProps['filterVal'] = { ...merged };
    return out;
  }, [data?.filter, filterVal]);

  /**
   * 与 `buildPrTrendPayload` / `trackData` 对齐：Service 子组件只收到 `data`（含 `isDateChanged`），
   * 不会单独透传 props.isDateChanged；若此处恒为 false 会误走「月 7 条」裁剪，与完整区间请求不一致。
   */
  const effectiveIsDateChanged = useMemo(() => {
    if (typeof isDateChanged === 'boolean') {
      return isDateChanged;
    }
    return Boolean((data as Record<string, unknown> | undefined)?.isDateChanged);
  }, [data, isDateChanged]);

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const block = data?.[dataKey] as PrTrendServiceBlock | undefined;
  const eventBlock = eventDataKey
    ? (data?.[eventDataKey] as PrEventTimelineBlock | undefined)
    : undefined;

  const loading = Boolean(block?.loading || eventBlock?.loading);
  const isError =
    Boolean((block?.errorMsg || eventBlock?.errorMsg) && !loading);
  const payload = useMemo((): PrTrendPayload | undefined => {
    const raw = block?.data as Record<string, unknown> | undefined;
    if (!raw) return undefined;
    if (Array.isArray(raw.categories) && raw.categories.length > 0 && Array.isArray(raw.series)) {
      return raw as PrTrendPayload;
    }
    return formatPrTrendForChart(raw, {
      seriesName: isEn ? metricNameEn : metricNameCn,
    }) as PrTrendPayload;
  }, [block?.data, isEn, metricNameCn, metricNameEn]);

  /** 未在 Service.formatData 预处理时，在本组件内格式化事件时间线 */
  const eventPayload = useMemo(() => {
    if (!eventBlock?.data) return { eventData: undefined, chartEventSetting: undefined };
    const rawEvt = eventBlock.data as Record<string, unknown>;
    if (rawEvt?.chartEventSetting && rawEvt.eventData) {
      return {
        eventData: rawEvt.eventData as PrMetricEventData,
        chartEventSetting: rawEvt.chartEventSetting as PrMetricChartEventSetting,
      };
    }
    const fv = resolvedFilterVal ?? undefined;
    const out = formatPrMentionStatsTimeline(rawEvt as any, fv as any);
    return { eventData: out.eventData, chartEventSetting: out.chartEventSetting };
  }, [eventBlock?.data, resolvedFilterVal]);

  const title = useMemo(() => {
    if (payload?.title) return payload.title;
    return isEn ? titleEn : titleCn;
  }, [isEn, payload?.title, titleCn, titleEn]);

  const subTitle = useMemo(() => {
    if (payload?.subTitle) return payload.subTitle;
    const fallback = isEn ? subTitleEn : subTitleCn;
    return fallback || undefined;
  }, [isEn, payload?.subTitle, subTitleCn, subTitleEn]);

  const rawCategories = payload?.categories ?? [];
  const rawSeries = payload?.series ?? [];

  /** 默认展示窗口裁剪：月粒度最近7个月，日粒度最近15天（用户手动改过日期时不裁剪） */
  const { categories, series } = useMemo(
    () => trimPrTrendDefaultWindow(rawCategories, rawSeries, {
      timeGranularity: resolvedFilterVal?.time_granularity,
      isDateChanged: effectiveIsDateChanged,
    }),
    [rawCategories, rawSeries, resolvedFilterVal?.time_granularity, effectiveIsDateChanged],
  );
  const hasChartData = categories.length > 0 && series.length > 0;

  const handleEvolutionChartDownload = useCallback(() => {
    if (!categories.length || !series.length) return;
    const s0 = series[0];
    const dataSource = categories.map((cat, i) => ({
      date: cat,
      articles: Number(s0?.data?.[i] ?? 0) || 0,
    }));
    const fileName = buildPrBackendDownloadCsvFileName(
      (data || {}) as Record<string, unknown>,
      'Overview Articles',
    );
    const escapeCsvCell = (v: string | number): string => {
      const s = String(v);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const header = ['Date', 'Articles'].map(escapeCsvCell).join(',');
    const body = dataSource.map((r) => [r.date, r.articles].map(escapeCsvCell).join(',')).join('\r\n');
    const csv = `\uFEFF${header}\r\n${body}`;
    FileSaver.saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), fileName);
  }, [categories, series, data]);

  const { eventData, chartEventSetting } = eventPayload;

  /** 根据 formatRule / yaxFormatRule 构建格式化函数 */
  const valueFormatter = useMemo(
    () => (value: number) =>
      String(dataFormat({ value, formatRule, emptyValue: '-' })),
    [formatRule],
  );
  const yAxisFormatter = useMemo(
    () => (value: number) =>
      String(dataFormat({ value, formatRule: yaxFormatRule ?? formatRule, emptyValue: '-' })),
    [formatRule, yaxFormatRule],
  );

  /** 与 `/api/v2/dashboard/studio/pr/overview/trend` 的 bucket 及 EventWindows 坐标对齐（月粒度类目为 YYYY-MM） */
  const chartDateType = useMemo((): ParamsTimeType => {
    const g = resolvedFilterVal?.time_granularity;
    if (g === 'monthly') return 'monthly';
    if (g === 'weekly') return 'weekly';
    if (g === 'hourly') return 'hourly';
    return 'daily';
  }, [resolvedFilterVal?.time_granularity]);

  const chartNode = hasChartData ? (
    <div className="dbh-pr-trend-section">
      <PrMetricTrendChart
        title={title}
        subTitle={subTitle}
        categories={categories}
        series={series}
        dateType={chartDateType}
        loading={false}
        empty={false}
        height={chartHeight}
        hasDownload
        onDownload={() => {
          void handleEvolutionChartDownload();
        }}
        valueFormatter={valueFormatter}
        yAxisFormatter={yAxisFormatter}
        eventData={eventData}
        chartEventSetting={chartEventSetting}
        showEventDescriptionBubble={showEventDescriptionBubble}
        eventVisibilityToggleVisible={eventVisibilityToggleVisible}
      />
    </div>
  ) : null;

  return (
    <Abnormal
      className="dbh-pr-trend-section__state"
      isLoading={loading}
      isError={isError}
      isNoData={!loading && !isError && !hasChartData}
      style={{ minHeight: 360, background: '#fff' }}
      component={chartNode}
    />
  );
};

export default PrTrendSection;
